"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Phone, CheckCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";

export function Hero() {
  // Use a single state to determine if video should be shown
  const [shouldUseVideo, setShouldUseVideo] = useState(false);

  useEffect(() => {
    // Set this once on mount - no re-renders/flashes
    try {
      // Check if desktop or good connection
      if (window.innerWidth >= 1024) {
        // For desktop devices, assume faster connections
        setShouldUseVideo(true);
      }
    } catch (error) {
      // Fallback if anything goes wrong
      console.error("Error determining video playback capability:", error);
    }
  }, []);

  return (
    <section className="relative py-16 lg:py-24 overflow-hidden">
      {/* Video Background with Fallback Image */}
      <div className="absolute inset-0 w-full h-full z-0">
        {/* Always show image for immediate display, then conditionally show video on top */}
        <div className="relative w-full h-full">
          <Image
            src="/images/axfalk-kranar-178.webp"
            alt="Axfalk kranservice bakgrundsbild"
            fill
            sizes="100vw"
            priority
            className="object-cover"
          />
        </div>

        {/* Video only loaded and shown for desktop devices */}
        {shouldUseVideo && (
          <video
            poster="/images/axfalk-kranar-178.webp"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover z-5"
          >
            <source
              src="https://axfalk.b-cdn.net/axfalk-videos/hero/hero-video.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        )}
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/70 z-10"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <Badge className="bg-blue-600 text-white px-4 py-2 text-sm font-medium border-0">
                Över 40 års erfarenhet
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Professionellt{" "}
                <span className="text-blue-400">montage och krantjänster i Linköping & Östergötland</span>
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed max-w-2xl">
                Axfalk AB levererar kompletta lösningar inom kranar och
                lyftanordningar. Från installation till underhåll - vi är din
                pålitliga partner för säkra och effektiva lyftlösningar.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Certifierad personal",
                "24/7 support",
                "Hela Sverige",
                "Kostnadsfri offert",
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <span className="text-white font-medium">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <Link
                href="/kontakta-oss"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl border-2 border-blue-600 no-underline"
              >
                Få Kostnadsfri Offert
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link
                href="tel:0709380356"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold text-lg border-2 border-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl no-underline"
              >
                <Phone className="h-5 w-5" />
                Ring: 070-938 03 56
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8 border-t border-white/20">
              <p className="text-sm text-gray-300 mb-4">Vi samarbetar med:</p>
              <div className="flex flex-wrap gap-6 items-center">
                {[
                  "Misia",
                  "Lyftab",
                  "Craneteam",
                  "Linköpings Byggdetaljer AB",
                ].map((brand, index) => (
                  <div
                    key={index}
                    className="text-gray-200 font-semibold text-lg"
                  >
                    {brand}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column intentionally left empty */}
        </div>
      </div>

      {/* Additional visual elements can be added here if needed */}
    </section>
  );
}
